export const VALID_SIZE_OPTIONS = [
  'xsmall',
  'small', 
  'medium',
  'large',
  'xlarge',
] as const;
export const NUMBERED_XLARGE_REGEX = /^[0-9]+xlarge$/;

export type ValidSizeOption = typeof VALID_SIZE_OPTIONS[number] | `${number}xlarge`;

export function validateSizeName(sizeName: string): {
  isValid: boolean;
  extractedSize: ValidSizeOption | null;
  message: string;
} {
  if (typeof sizeName !== 'string') {
    return {
      isValid: false,
      extractedSize: null,
      message: 'Size name must be a string'
    };
  }

  if (!sizeName.trim()) {
    return {
      isValid: false,
      extractedSize: null,
      message: 'Size name cannot be empty'
    };
  }

  if (/[A-Z]/.test(sizeName)) {
    return {
      isValid: false,
      extractedSize: null,
      message: 'Size name must not contain uppercase letters.'
    };
  }

  if (/[^a-z0-9_-]/.test(sizeName)) {
    return {
      isValid: false,
      extractedSize: null,
      message: 'Size name contains invalid characters. Only lowercase letters, numbers, hyphens (-), and underscores (_) are allowed.'
    };
  }

  const normalizedSize = sizeName.toLowerCase().trim();
  
  if (VALID_SIZE_OPTIONS.includes(normalizedSize as any) || NUMBERED_XLARGE_REGEX.test(normalizedSize)) {
    return {
      isValid: true,
      extractedSize: normalizedSize as ValidSizeOption,
      message: `Valid size: ${normalizedSize}`
    };
  }

  // Apply the same regex patterns as the cloud server.
  const withPrefixRegex = /-(\w+)/;
  const withSuffixRegex = /(\w+)-/;
  const withPrefixAndSuffix = /-(\w+)-/;

  const regexPatterns = [withPrefixRegex, withSuffixRegex, withPrefixAndSuffix];

  for (const regex of regexPatterns) {
    const match = normalizedSize.match(regex);
    if (match && (VALID_SIZE_OPTIONS.includes(match[1] as any) || NUMBERED_XLARGE_REGEX.test(match[1]))) {
      return {
        isValid: true,
        extractedSize: match[1] as ValidSizeOption,
        message: `Valid size with prefix/suffix: ${match[1]} (from "${normalizedSize}")`
      };
    }
  }

  return {
    isValid: false,
    extractedSize: null,
    message: `Invalid size name. Please use a valid size name such as xsmall, small, medium, large, xlarge, [number]xlarge (e.g., 3xlarge, 10xlarge), or with a prefix/suffix.`
  };
}

export function extractSizeFromName(sizeName: string): ValidSizeOption | null {
  const result = validateSizeName(sizeName);
  return result.extractedSize;
}


export function getSizeExamples(): string[] {
  return [
    'prefix-xsmall-suffix',
    'prefix-small',
    'medium-suffix', 
    'prefix123_morePrefix-large',
    'xlarge-suffix_moreSuffix123',
    'prefix-10xlarge-suffix',
  ];
} 